/* eslint-disable react/prop-types */
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye, PenLine, RefreshCcw, UserX } from "lucide-react";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { DeleteConfirmDialog } from "@/components/dialog/common-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import hasPermission from "@/hooks/hasPermission";
import PointBaseConfirmDialog from "./PointBaseConfirmDialog";

const formatDate = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString();
};

export function CustomerTable({
  tableData,
  onTogglePointBase,
  togglingCustomerId,
  onCustomerStatusChange,
  statusUpdatingId,
  statusUpdateLoading,
  onEditCustomer,
}) {
  const hasPointBase = hasPermission("back_office.customer.point_base");

  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    customerId: null,
    customerName: "",
    actionType: "ban",
    addressId: null,
    isCustomerBanned: false,
    isAddressBanned: false,
  });
  const [isBanAddress, setIsBanAddress] = useState(false);
  const [isUnbanCustomer, setIsUnbanCustomer] = useState(false);
  const [isUnbanAddress, setIsUnbanAddress] = useState(false);

  const [pointBaseModalState, setPointBaseModalState] = useState({
    isOpen: false,
    customer: null,
  });

  const openPointBaseModal = (customer) => {
    setPointBaseModalState({
      isOpen: true,
      customer,
    });
  };

  const closePointBaseModal = () => {
    setPointBaseModalState({
      isOpen: false,
      customer: null,
    });
  };

  const openStatusModal = (customer) => {
    setConfirmState({
      isOpen: true,
      customerId: customer?.id,
      customerName: customer?.full_name || "this customer",
      actionType: customer?.is_banned ? "active" : "ban",
      addressId: customer?.active_address?.id,
      isCustomerBanned: customer?.is_banned,
      isAddressBanned: customer?.active_address?.is_banned,
    });
    setIsUnbanCustomer(!!customer?.is_banned);
    setIsUnbanAddress(!!customer?.active_address?.is_banned);
  };

  const closeStatusModal = () => {
    setConfirmState((prev) => ({ ...prev, isOpen: false }));
    setIsBanAddress(false);
    setIsUnbanCustomer(false);
    setIsUnbanAddress(false);
  };

  const isBanAction = confirmState.actionType === "ban";

  return (
    <>
      <div className="overflow-x-auto">
        <Table className="w-full">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">ID</TableHead>
              <TableHead>Full Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Join Date</TableHead>
              <TableHead>Postcode</TableHead>
              {hasPointBase && <TableHead>Point Base</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableData?.length === 0 && (
              <TableRow>
                <TableCell colSpan={hasPointBase ? 9 : 8} className="text-center text-cwhite2">
                  No customer found
                </TableCell>
              </TableRow>
            )}

            {tableData?.length > 0 &&
              tableData.map((person) => {
                const isPointBase = Boolean(person?.is_point_base);
                const isSwitchLoading = togglingCustomerId === person?.id;
                const activeAddress = person?.active_address;
                const statusLabel = person?.is_banned ? "Banned" : "Active";
                const isStatusButtonLoading =
                  statusUpdateLoading && statusUpdatingId === person?.id;

                return (
                  <TableRow
                    key={person?.id}
                    className={
                      person?.is_banned ? "bg-cred/20 hover:bg-cred/20" : ""
                    }
                  >
                    <TableCell className="font-medium text-cwhite">
                      {person?.unique_id || "N/A"}
                    </TableCell>
                    <TableCell className="text-cwhite">
                      {person?.full_name || "N/A"}
                    </TableCell>
                    <TableCell className="text-cwhite2">
                      {person?.number || "N/A"}
                    </TableCell>
                    <TableCell className="text-cwhite2">
                      {person?.email || "N/A"}
                    </TableCell>
                    <TableCell className="max-w-[260px] text-cwhite2">
                      {formatDate(person?.active_address?.created_at)}
                    </TableCell>
                    <TableCell className="text-cwhite2">
                      {activeAddress?.postal_code || "N/A"}
                    </TableCell>
                    {hasPointBase && (
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={isPointBase}
                            disabled={isSwitchLoading}
                            onCheckedChange={() =>
                              openPointBaseModal(person)
                            }
                          />
                          <span className="text-sm text-cwhite2">
                            {/* {isPointBase ? "Enabled" : "Disabled"} */}
                          </span>
                        </div>
                      </TableCell>
                    )}
                    <TableCell
                      className={`${person?.is_banned ? "text-cred" : "text-cgreen"}`}
                    >
                      {statusLabel}
                    </TableCell>

                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/backoffice/customer/customer/${person?.id}`}
                        >
                          <Eye
                            size={18}
                            className="cursor-pointer text-cwhite2"
                          />
                        </Link>
                        {
                          hasPermission("back_office.customer.update_customer") && (
                            <Button
                              type="button"
                              variant="ghost"
                              className="h-auto p-0 text-cyellow hover:bg-transparent hover:text-cyellow"
                              onClick={() => onEditCustomer?.(person)}
                            >
                              <PenLine size={18} className="cursor-pointer" />
                            </Button>)}

                        {!person?.is_banned ? (
                          <>
                            {
                              hasPermission("back_office.customer.ban_customer") && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  disabled={isStatusButtonLoading}
                                  className="h-auto p-0 text-cred hover:bg-transparent hover:text-cred"
                                  onClick={() => openStatusModal(person)}
                                >
                                  <UserX size={18} className="cursor-pointer" />
                                </Button>
                              )
                            }
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            disabled={isStatusButtonLoading}
                            className="h-auto p-0 text-cgreen hover:bg-transparent hover:text-cgreen"
                            onClick={() => openStatusModal(person)}
                          >
                            <RefreshCcw size={18} className="cursor-pointer" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>

      <DeleteConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={closeStatusModal}
        onConfirm={() =>
          onCustomerStatusChange?.({
            customerId: confirmState.customerId,
            actionType: confirmState.actionType,
            banAddress: isBanAddress,
            unbanCustomer: isUnbanCustomer,
            unbanAddress: isUnbanAddress,
            addressId: confirmState.addressId,
          })
        }
        icon={isBanAction ? <UserX /> : <RefreshCcw />}
        title={isBanAction ? "Confirm Customer Ban" : "Confirm Customer Active"}
        description={
          isBanAction
            ? `Are you sure you want to ban ${confirmState.customerName}?`
            : `Are you sure you want to activate ${confirmState.customerName}?`
        }
        yesBtnTxt={isBanAction ? "Yes, Ban" : "Yes, Activate"}
        noBtnTxt="Cancel"
        yesBtnClass={isBanAction ? "bg-cred" : "bg-cgreen text-black"}
        isLoading={
          statusUpdateLoading && statusUpdatingId === confirmState.customerId
        }
      >
        {isBanAction && (
          <div className="flex items-center space-x-2 mt-4 justify-center">
            <Checkbox
              id="banAddress"
              checked={isBanAddress}
              onCheckedChange={setIsBanAddress}
            />
            <label
              htmlFor="banAddress"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white cursor-pointer"
            >
              Also ban this customer's address
            </label>
          </div>
        )}
        {!isBanAction && (
          <div className="flex items-center space-x-2 mt-4 justify-center">
            <Checkbox
              id="unbanAddress"
              checked={isUnbanAddress}
              onCheckedChange={setIsUnbanAddress}
            />
            <label
              htmlFor="unbanAddress"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white cursor-pointer"
            >
              Also unban this customer's address
            </label>
          </div>
        )}
      </DeleteConfirmDialog>

      <PointBaseConfirmDialog
        isOpen={pointBaseModalState.isOpen}
        onClose={closePointBaseModal}
        onConfirm={() => {
          if (pointBaseModalState.customer?.id) {
            onTogglePointBase?.(pointBaseModalState.customer.id);
          }
        }}
        isPointBaseActive={Boolean(pointBaseModalState.customer?.is_point_base)}
        isLoading={
          togglingCustomerId === pointBaseModalState.customer?.id
        }
      />
    </>
  );
}
